import {
  type ResetPasswordParams,
  resetPasswordSchema,
  verifySchema,
} from '@argus-labs/auth-shared/auth'
import { VerifyType } from '@argus-labs/auth-shared/verify'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PageCard } from '@/components/ui/page-card'
import { authClient } from '@/lib/auth-client'
import { otpVerification } from '@/lib/otp-verification'
import { toast } from '@/lib/toast'
import { BETTER_AUTH_EMAIL_OTP_ERROR_CODES, commonSearchSchema, generateTitle } from '@/lib/utils'

const forgotPasswordPageSchema = z.object({
  ...commonSearchSchema.shape,
  ...verifySchema.shape,
})

export const Route = createFileRoute('/_signedOut/forgot-password')({
  validateSearch: forgotPasswordPageSchema,
  head: () => ({ meta: [{ title: generateTitle('Forgot Password') }] }),
  beforeLoad: async ({ search }) => {
    if (!search.otp || !search.email) {
      throw redirect({ to: '/login' })
    }
  },
  component: ForgotPassword,
})

function ForgotPassword() {
  return (
    <PageCard title="Forgot Password" description="Enter your password to create a new one.">
      <ForgotPasswordForm />
    </PageCard>
  )
}

function ForgotPasswordForm() {
  const { otp, email } = Route.useSearch()

  const form = useForm<ResetPasswordParams>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '' },
  })
  const navigate = useNavigate()

  const onSubmit = async (values: ResetPasswordParams) => {
    try {
      const { data, error } = await authClient.emailOtp.resetPassword({
        email,
        otp,
        password: values.password,
      })

      // Reset successful
      if (data) {
        // Reset password doesn't automatically sign in the user
        // So we need to redirect to login page for user to sign in with the new password
        // We raised an issue but it wasn't fully implemented: https://github.com/better-auth/better-auth/issues/1814
        // Raised a new one: https://github.com/better-auth/better-auth/issues/6130
        return navigate({ to: '/login' })
      }

      // If OTP was expired or invalid, send a new one and redirect to verify page, so the user can redo
      if (
        error.code === BETTER_AUTH_EMAIL_OTP_ERROR_CODES.OTP_EXPIRED ||
        error.code === BETTER_AUTH_EMAIL_OTP_ERROR_CODES.INVALID_OTP
      ) {
        const { data, error: resendError } = await otpVerification.requestPasswordReset(email)

        if (data) {
          toast({
            description: 'OTP is expired or invalid. We’ve sent you a new one. Please try again.',
          })
          return navigate({ to: '/verify', search: { email, type: VerifyType.PasswordReset } })
        }

        toast.fromError(resendError ?? error)
        return
      }

      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2">
      <Input
        control={form.control}
        type="password"
        name="password"
        label="Password"
        required
        autoFocus
        autoComplete="new-password"
        placeholder=""
      />

      <Button type="submit" flex isDisabled={form.formState.isSubmitting}>
        Next
      </Button>
    </form>
  )
}
